import "../Home/Styles.css";
import LegalPageLinks from "../../components/LegalPageLinks";
import { CHURCH_INFO } from "../../utils/churchInfo";
import { usePageMeta } from "../../utils/usePageMeta";

const quickContacts = [
  {
    label: "Email Support",
    value: CHURCH_INFO.email,
    href: `mailto:${CHURCH_INFO.email}`,
    tone: "bg-tcc-blue text-white",
  },
  {
    label: "Call the Church Office",
    value: CHURCH_INFO.phoneDisplay,
    href: CHURCH_INFO.phoneHref,
    tone: "bg-tcc-green text-[#18320a]",
  },
  {
    label: "Visit Trinity Christian Church",
    value: `${CHURCH_INFO.addressLine1}, ${CHURCH_INFO.addressLine2}`,
    href: "https://maps.google.com/?q=20330+Superior+Rd+Taylor+MI+48180",
    tone: "bg-tcc-brown text-white",
  },
  {
    label: "Main Website",
    value: CHURCH_INFO.websiteLabel,
    href: CHURCH_INFO.websiteUrl,
    tone: "bg-white text-tcc-blue border border-tcc-blue/15",
  },
];

const faqs = [
  {
    question: "How do I watch sermons in the app?",
    answer:
      "Open the Sermons section, choose a message, and tap the watch button to begin playback. Some sermons may open through an in-app web view or linked media source.",
  },
  {
    question: "Why is a sermon video not playing?",
    answer:
      "Check your internet connection, close and reopen the app, and try again. If the issue continues, send support the sermon title, date, your device type, and what happened when you tapped play.",
  },
  {
    question: "How do sermon notes work?",
    answer:
      "Sermon notes are attached to individual messages when notes are available. Open the sermon and select the notes option if it appears.",
  },
  {
    question: "Why don’t I see sermon notes for every message?",
    answer:
      "Notes only appear when they have been added for that specific sermon. Some messages may include video only.",
  },
  {
    question: "How do I give through the app?",
    answer:
      "Open the giving area, choose an amount, and follow the secure checkout prompts. Giving may be processed through a trusted third-party provider such as PayPal.",
  },
  {
    question: "What should I do if PayPal checkout does not complete?",
    answer:
      "Try again after confirming your connection and payment details. If the issue continues, contact support with the amount you attempted, the approximate time, and your device type.",
  },
  {
    question: "How do I submit a prayer request?",
    answer:
      "Open the Prayer section in the app and complete the request form. If the form does not send properly, email the church office for assistance.",
  },
  {
    question: "Why is content not loading?",
    answer:
      "Refresh the screen, close and reopen the app, and confirm that your device has internet access. If content still does not load, include the affected screen and your app version when contacting support.",
  },
  {
    question: "What should I do if events or announcements look incorrect?",
    answer:
      "Please contact support and tell us which screen you were viewing and what content appears outdated or incorrect.",
  },
];

const Support = () => {
  usePageMeta(
    "Trinity Christian Church App Support",
    "Get help with the Trinity Christian Church mobile app, including sermons, sermon notes, events, prayer requests, announcements, and secure giving."
  );

  return (
    <main className="min-h-screen bg-[#f4f1ec] text-slate-900">
      <section className="relative overflow-hidden bg-[#141414]">
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-3">
          <div className="bg-tcc-blue/35" />
          <div className="bg-tcc-green/25" />
          <div className="bg-tcc-brown/35" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.15),_transparent_38%)]" />
        <div className="relative mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-24">
          <a
            href="/"
            className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm font-medium text-white/85 backdrop-blur-sm transition hover:border-white/40 hover:bg-white/10 hover:text-white"
          >
            Back to Home
          </a>

          <div className="mt-10 grid gap-10 lg:grid-cols-[1.35fr_0.9fr] lg:items-end">
            <div>
              <p className="text-sm uppercase tracking-[0.45em] text-white/70">
                Trinity Christian Church App
              </p>
              <h1 className="mt-4 text-4xl text-white md:text-6xl">
                <span className="archivo-black-regular">App</span>{" "}
                <i className="anton-regular-italic text-tcc-green">Support</i>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-white/85">
                Need help with sermons, sermon notes, events, prayer requests,
                announcements, or giving? We&apos;re here to help.
              </p>
              <p className="mt-5 max-w-3xl text-base leading-8 text-white/75">
                The Trinity Christian Church app helps members and guests stay
                connected throughout the week. You can watch sermons, view sermon
                notes, explore upcoming events, send prayer requests, receive
                announcements, and give securely through the app.
              </p>
              <LegalPageLinks current="support" />
            </div>

            <div className="rounded-[2rem] border border-white/15 bg-white/10 p-6 backdrop-blur-md">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">
                App Store Support URL
              </p>
              <p className="mt-3 text-2xl font-semibold text-white">
                Public help for reviewers and app users
              </p>
              <div className="mt-5 space-y-3 text-sm text-white/80">
                <p>No login required</p>
                <p>Mobile-friendly and public</p>
                <p>Privacy policy linked below</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-8 max-w-6xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {quickContacts.map((contact) => (
            <a
              key={contact.label}
              href={contact.href}
              target={contact.href.startsWith("http") ? "_blank" : undefined}
              rel={contact.href.startsWith("http") ? "noopener noreferrer" : undefined}
              className={`rounded-[2rem] p-6 shadow-[0_18px_55px_rgba(0,0,0,0.08)] transition hover:-translate-y-1 ${contact.tone}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.22em] opacity-75">
                {contact.label}
              </p>
              <p className="mt-4 text-xl font-semibold leading-8">{contact.value}</p>
            </a>
          ))}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-blue">
              Quick Help
            </span>
            <h2 className="mt-4 text-3xl text-tcc-blue archivo-black-regular">
              Need immediate support?
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              If you are reporting an app problem, please include your device
              type, app version, the screen where the issue happened, what you
              tapped, what you expected to happen, and what happened instead.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`mailto:${CHURCH_INFO.email}?subject=Trinity%20Christian%20Church%20App%20Support`}
                className="rounded-full bg-tcc-blue px-5 py-3 font-semibold text-white transition hover:bg-opacity-90"
              >
                Email Support
              </a>
              <a
                href="tel:+17347207189"
                className="rounded-full bg-tcc-green px-5 py-3 font-semibold text-[#18320a] transition hover:bg-opacity-90"
              >
                Call the Church Office
              </a>
              <a
                href="/privacy"
                className="rounded-full border border-tcc-blue/20 bg-white px-5 py-3 font-semibold text-tcc-blue transition hover:border-tcc-blue/40"
              >
                Privacy Policy
              </a>
            </div>
          </div>

          <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_80px_rgba(6,76,124,0.18)]">
            <div className="bg-gradient-to-br from-tcc-blue via-tcc-green to-tcc-brown p-8">
              <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white">
                Contact Details
              </span>
              <div className="mt-6 space-y-5 text-white">
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/70">
                    Email
                  </p>
                  <a
                    href={`mailto:${CHURCH_INFO.email}`}
                    className="mt-2 block text-xl font-semibold"
                  >
                    {CHURCH_INFO.email}
                  </a>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/70">
                    Phone
                  </p>
                  <a
                    href={CHURCH_INFO.phoneHref}
                    className="mt-2 block text-xl font-semibold"
                  >
                    {CHURCH_INFO.phoneDisplay}
                  </a>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/70">
                    Address
                  </p>
                  <p className="mt-2 text-lg font-semibold">
                    {CHURCH_INFO.addressLine1}
                  </p>
                  <p className="text-white/85">{CHURCH_INFO.addressLine2}</p>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/70">
                    Website
                  </p>
                  <a
                    href={CHURCH_INFO.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 block text-lg font-semibold"
                  >
                    {CHURCH_INFO.websiteLabel}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <section className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-green/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#5f9024]">
              Giving Support
            </span>
            <h2 className="mt-4 text-3xl text-[#5f9024] archivo-black-regular">
              Secure giving help
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              Giving through the app is intended to be processed securely through
              a trusted payment partner. If a donation attempt does not complete,
              do not keep retrying without checking whether a charge already went
              through.
            </p>
            <ul className="mt-6 space-y-3 text-base leading-8 text-slate-700">
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-green" />
                <span>Check whether you received a payment confirmation or receipt.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-green" />
                <span>If PayPal or checkout fails, send the attempted amount, time, and device type to support.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-green" />
                <span>
                  For receipt questions or donation concerns, contact{" "}
                  {CHURCH_INFO.email} or call the church office.
                </span>
              </li>
            </ul>
          </section>

          <section className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-brown/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-brown">
              Sermons and Media
            </span>
            <h2 className="mt-4 text-3xl text-tcc-brown archivo-black-regular">
              Sermons, notes, and media issues
            </h2>
            <ul className="mt-6 space-y-3 text-base leading-8 text-slate-700">
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-brown" />
                <span>If a sermon video does not play, include the title and date when contacting support.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-brown" />
                <span>Sermon notes may not appear for every message. Notes only display when they have been added for that sermon.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-brown" />
                <span>If a note, PDF, video, or link looks outdated or broken, tell us which screen you were on and what looked wrong.</span>
              </li>
            </ul>
          </section>
        </div>

        <section className="mt-8 rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex rounded-full bg-tcc-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-blue">
                Frequently Asked Questions
              </span>
              <h2 className="mt-4 text-3xl text-tcc-blue archivo-black-regular">
                Common support questions
              </h2>
            </div>
            <div className="rounded-3xl bg-[#f7f9fb] px-5 py-4 text-sm leading-7 text-slate-600">
              This page is public and intended for App Store review, church
              members, guests, donors, and anyone needing app help.
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={faq.question}
                className="group overflow-hidden rounded-3xl border border-black/5 bg-[#faf8f5] open:bg-white"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5">
                  <span className="text-left text-lg font-semibold text-slate-900">
                    {faq.question}
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${
                      index % 3 === 0
                        ? "bg-tcc-blue/10 text-tcc-blue"
                        : index % 3 === 1
                          ? "bg-tcc-green/15 text-[#5f9024]"
                          : "bg-tcc-brown/10 text-tcc-brown"
                    }`}
                  >
                    Open
                  </span>
                </summary>
                <div className="px-6 pb-6 text-base leading-8 text-slate-700">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-blue">
              Technical Issue Reporting
            </span>
            <h2 className="mt-4 text-3xl text-tcc-blue archivo-black-regular">
              What to send when reporting a bug
            </h2>
            <ul className="mt-6 space-y-3 text-base leading-8 text-slate-700">
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>Device model</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>iPhone or Android</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>App version if visible</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>The screen where the issue happened</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>What you tapped</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>What happened instead of what you expected</span>
              </li>
            </ul>
          </div>

          <div className="overflow-hidden rounded-[2rem] shadow-[0_24px_80px_rgba(6,76,124,0.18)]">
            <div className="bg-gradient-to-r from-tcc-blue via-tcc-green to-tcc-brown p-8">
              <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white">
                Helpful Links
              </span>
              <h2 className="mt-4 text-3xl text-white archivo-black-regular">
                More church resources
              </h2>
              <p className="mt-4 text-base leading-8 text-white/90">
                Use these links if you need privacy details or information about
                church-owned content and media.
              </p>
            </div>
            <div className="bg-white p-8">
              <div className="space-y-4">
                <a
                  href="/privacy"
                  className="block rounded-3xl border border-tcc-blue/10 bg-[#f7f9fb] px-5 py-4 font-semibold text-tcc-blue"
                >
                  Privacy Policy
                </a>
                <a
                  href="/copyright"
                  className="block rounded-3xl border border-tcc-blue/10 bg-[#f7f9fb] px-5 py-4 font-semibold text-tcc-blue"
                >
                  Copyright and Content Use
                </a>
              </div>
            </div>
          </div>
        </section>
      </section>
    </main>
  );
};

export default Support;
